import { motion } from 'framer-motion';
import { Clock, ShieldCheck, Globe2, MessageCircle, Heart, MapPin, Users, Calendar } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import JoinzyLogo from './joinzy_new.png'; // Nokta-eğik çizgi "burada" demek
// High quality placeholder images
const avatarImages = {
  user1: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
  user2: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
  user3: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100&h=100&fit=crop',
};

const JOINZY_LOGO = JoinzyLogo;

export const Features = () => {
  const { t } = useLanguage();

  return (
    <section 
      id="features" 
      className="relative py-24 md:py-32 bg-[#0B0E14]"
      data-testid="features-section"
    >
      {/* Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(to right, #008080 1px, transparent 1px),
                           linear-gradient(to bottom, #008080 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="inline-block font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-4"
          >
            {t('nav.howItWorks')}
          </motion.span>
          <h2 
            className="font-montserrat font-bold text-3xl md:text-4xl lg:text-5xl text-white tracking-tight mb-6"
            data-testid="features-title"
          >
            {t('features.title')}
          </h2>
          <p className="font-poppins text-white/60 max-w-2xl mx-auto text-base md:text-lg">
            {t('features.subtitle')}
          </p>
        </motion.div>

        {/* App Demo Section - 3 Phones SIDE BY SIDE */}
        <div className="flex flex-col lg:flex-row items-center justify-center gap-6 lg:gap-8 mb-20">
          {/* Phone 1: Profile Screen */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center"
          >
            <PhoneMockup>
              <ProfileScreen />
            </PhoneMockup>
            <div className="mt-6 text-center max-w-[200px]">
              <h3 className="font-montserrat font-bold text-lg text-white mb-2">
                {t('features.phone1Title')}
              </h3>
              <p className="font-poppins text-white/60 text-sm">
                {t('features.phone1Desc')}
              </p>
            </div>
          </motion.div>

          {/* Phone 2: Post Detail - Featured/Center */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col items-center lg:-mt-8"
          >
            <PhoneMockup featured>
              <PostDetailScreen />
            </PhoneMockup>
            <div className="mt-6 text-center max-w-[200px]">
              <h3 className="font-montserrat font-bold text-lg text-white mb-2">
                {t('features.phone2Title')}
              </h3>
              <p className="font-poppins text-white/60 text-sm">
                {t('features.phone2Desc')}
              </p>
            </div>
          </motion.div>

          {/* Phone 3: Moments Feed */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col items-center"
          >
            <PhoneMockup>
              <MomentsFeedScreen />
            </PhoneMockup>
            <div className="mt-6 text-center max-w-[200px]">
              <h3 className="font-montserrat font-bold text-lg text-white mb-2">
                {t('features.phone3Title')}
              </h3>
              <p className="font-poppins text-white/60 text-sm">
                {t('features.phone3Desc')}
              </p>
            </div>
          </motion.div>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeatureCard
            icon={Clock}
            title={t('features.card1.title')}
            description={t('features.card1.desc')}
            gradient="from-[#008080]/20"
            delay={0}
          />
          <FeatureCard
            icon={Globe2}
            title={t('features.card2.title')}
            description={t('features.card2.desc')}
            gradient="from-[#6366F1]/20"
            delay={0.1}
            featured
          />
          <FeatureCard
            icon={ShieldCheck}
            title={t('features.card3.title')}
            description={t('features.card3.desc')}
            gradient="from-[#10B981]/20"
            delay={0.2}
          />
        </div>

        {/* CTA Banner */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20"
        >
          <div className="relative rounded-3xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-[#008080] to-[#004D4D]" />
            <div className="relative px-8 py-12 md:px-16 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h3 className="font-montserrat font-bold text-2xl md:text-3xl text-white mb-3">
                  {t('features.ctaTitle')}
                </h3>
                <p className="font-poppins text-white/70 text-base md:text-lg">
                  {t('features.ctaDesc')}
                </p>
              </div>
              <motion.a
                href="#download"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white rounded-full font-poppins font-semibold text-[#008080] shadow-lg hover:shadow-xl transition-shadow whitespace-nowrap"
                data-testid="features-cta-button"
              >
                {t('features.ctaButton')}
              </motion.a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Phone Mockup Component
const PhoneMockup = ({ children, featured = false }) => (
  <div className={`relative ${featured ? 'scale-110 z-10' : 'scale-100'}`} style={{ width: '220px' }}>
    {/* Phone Frame */}
    <div className="relative bg-[#1A1A1A] rounded-[2rem] p-1.5 shadow-2xl border border-white/10">
      {/* Notch */}
      <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-16 h-4 bg-black rounded-full z-10" />
      
      {/* Screen */}
      <div className="bg-[#0B0E14] rounded-[1.75rem] overflow-hidden relative" style={{ aspectRatio: '9/19.5' }}>
        {children}
      </div>
    </div>
    
    {/* Glow Effect */}
    {featured && (
      <div className="absolute -inset-4 bg-gradient-to-b from-[#00E5FF]/20 to-transparent rounded-[3rem] blur-xl -z-10" />
    )}
  </div>
);

// Profile Screen Component (Based on Screenshot 1)
const ProfileScreen = () => (
  <div className="h-full flex flex-col text-[10px]">
    {/* Profile Header with Image */}
    <div className="relative h-[50%]">
      <img 
        src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=500&fit=crop"
        alt="Profile"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-transparent to-transparent" />
      
      {/* Profile Info Overlay */}
      <div className="absolute bottom-2 left-2 right-2">
        <h3 className="font-montserrat font-bold text-white text-sm">Emma <span className="font-normal text-white/60">· 24 · Leo</span></h3>
        <div className="flex items-center gap-1 mt-1">
          <span className="px-1.5 py-0.5 bg-[#008080] text-white text-[8px] rounded-full">#joinzy</span>
        </div>
        <div className="flex gap-1 mt-2">
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/10 backdrop-blur rounded-full text-white/70 text-[8px]">
            <Users className="w-2 h-2" /> 12 friends
          </span>
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/10 backdrop-blur rounded-full text-white/70 text-[8px]">
            <Heart className="w-2 h-2" /> 3 partners
          </span>
        </div>
        <div className="flex gap-1 mt-2">
          <button className="flex-1 py-1.5 bg-[#008080] rounded-full text-white text-[8px] font-semibold flex items-center justify-center gap-0.5">
            <MessageCircle className="w-2 h-2" /> Chat
          </button>
          <button className="flex-1 py-1.5 bg-white/10 backdrop-blur rounded-full text-white text-[8px] font-semibold">
            Relationship
          </button>
        </div>
      </div>
    </div>
    
    {/* Tabs */}
    <div className="flex border-b border-white/10">
      <button className="flex-1 py-2 text-[#00E5FF] text-[8px] font-semibold border-b border-[#00E5FF]">Posts</button>
      <button className="flex-1 py-2 text-white/50 text-[8px] font-semibold">Moments</button>
      <button className="flex-1 py-2 text-white/50 text-[8px] font-semibold">Tagged</button>
    </div>
    
    {/* Posts Grid */}
    <div className="flex-1 p-1 grid grid-cols-3 gap-0.5">
      <div className="relative aspect-square rounded overflow-hidden bg-white/5">
        <img src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=100&h=100&fit=crop" alt="Post" className="w-full h-full object-cover" />
        <span className="absolute top-0.5 left-0.5 px-1 py-0.5 bg-[#008080]/90 text-white text-[6px] rounded">#gym</span>
      </div>
      <div className="relative aspect-square rounded overflow-hidden bg-white/5">
        <img src="https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=100&h=100&fit=crop" alt="Post" className="w-full h-full object-cover" />
      </div>
      <div className="relative aspect-square rounded overflow-hidden bg-white/5">
        <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=100&h=100&fit=crop" alt="Post" className="w-full h-full object-cover" />
      </div>
    </div>
    
    {/* Bottom Nav */}
    <BottomNav activeIndex={4} />
  </div>
);

// Post Detail Screen Component (Based on Screenshot 2)
const PostDetailScreen = () => (
  <div className="h-full flex flex-col text-[10px]">
    {/* Header */}
    <div className="flex items-center justify-between px-2 py-2 pt-6">
      <img src={JOINZY_LOGO} alt="Joinzy" className="h-5" />
      <div className="w-4 h-4 rounded-full bg-white/10 flex items-center justify-center">
        <span className="text-[8px]">🔔</span>
      </div>
    </div>
    
    {/* Main Content */}
    <div className="flex-1 relative">
      <img 
        src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=300&h=400&fit=crop"
        alt="Post"
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-transparent to-transparent" />
      
      {/* Post Info */}
      <div className="absolute bottom-0 left-0 right-0 p-2">
        <h3 className="font-montserrat font-bold text-white text-base mb-0.5">
          Alex <span className="font-normal text-white/60 text-sm">28</span>
        </h3>
        <div className="flex flex-wrap gap-1 mb-2">
          <span className="px-1.5 py-0.5 bg-[#008080]/80 text-white text-[8px] rounded-full">#hiking</span>
          <span className="px-1.5 py-0.5 bg-[#008080]/80 text-white text-[8px] rounded-full">#travel</span>
        </div>
        
        <p className="text-white/70 text-[8px] mb-1">Looking for</p>
        <div className="flex flex-wrap gap-1 mb-2">
          <span className="px-2 py-1 border border-[#00E5FF] text-[#00E5FF] text-[8px] rounded-full">♂ Male</span>
          <span className="px-2 py-1 border border-pink-400 text-pink-400 text-[8px] rounded-full">♀ Female</span>
          <span className="px-2 py-1 border border-[#10B981] text-[#10B981] text-[8px] rounded-full">👥 Any</span>
        </div>
        
        <div className="flex items-center gap-2 text-white/50 text-[8px]">
          <span className="flex items-center gap-0.5"><Users className="w-2 h-2" /> 25-42</span>
          <span className="flex items-center gap-0.5"><MapPin className="w-2 h-2" /> &lt; 5 km</span>
        </div>
      </div>
    </div>
    
    {/* Bottom Nav */}
    <BottomNav activeIndex={0} />
  </div>
);

// Moments Feed Screen Component (Based on Screenshot 3)
const MomentsFeedScreen = () => {
  const moments = [
    { name: 'Sofia', location: 'Barcelona', time: '15:42', text: 'Beach trip this weekend!', tag: '#surfing', avatar: avatarImages.user1 },
    { name: 'Marcus', location: 'Berlin', time: '14:30', text: 'Looking for concert buddies', tag: '#livemusic', avatar: avatarImages.user2 },
  ];

  return (
    <div className="h-full flex flex-col bg-white text-[10px]">
      {/* Header */}
      <div className="flex items-center justify-center px-2 py-2 pt-6 border-b border-gray-100">
        <span className="font-montserrat font-bold text-[#222] text-sm">Moments</span>
      </div>
      
      {/* Moments List */}
      <div className="flex-1 overflow-hidden">
        {moments.map((moment, i) => (
          <div key={i} className="p-2 border-b border-gray-100">
            <div className="flex items-start gap-2">
              <img src={moment.avatar} alt={moment.name} className="w-8 h-8 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="font-semibold text-[#222] text-[10px]">{moment.name}</span>
                  <span className="text-gray-400 text-[8px] flex items-center gap-0.5">
                    <MapPin className="w-2 h-2" /> {moment.location} · {moment.time}
                  </span>
                </div>
                <p className="text-[#222] text-[10px] mt-0.5 truncate">{moment.text}</p>
                <span className="inline-block px-1.5 py-0.5 bg-[#008080]/10 text-[#008080] text-[8px] rounded-full mt-1">{moment.tag}</span>
                
                <p className="text-gray-400 text-[7px] mt-1">Looking for</p>
                <div className="flex flex-wrap gap-0.5 mt-0.5">
                  <span className="px-1 py-0.5 border border-[#00E5FF] text-[#00E5FF] text-[6px] rounded-full">♂</span>
                  <span className="px-1 py-0.5 border border-pink-400 text-pink-400 text-[6px] rounded-full">♀</span>
                  <span className="px-1 py-0.5 border border-[#10B981] text-[#10B981] text-[6px] rounded-full">👥</span>
                </div>
                
                <div className="flex items-center gap-2 mt-1 text-gray-400 text-[7px]">
                  <span><Users className="w-2 h-2 inline" /> 18-55</span>
                  <span><Heart className="w-2 h-2 inline" /> 0</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Bottom Nav */}
      <BottomNav activeIndex={1} light />
    </div>
  );
};

// Bottom Navigation Component - Matches actual app design
const BottomNav = ({ activeIndex = 0, light = false }) => (
  <div className={`flex items-center justify-around py-2 ${light ? 'bg-white border-t border-gray-100' : 'bg-white'}`}>
    {/* Home */}
    <div className={`p-2 rounded-xl ${activeIndex === 0 ? 'bg-[#008080]/10' : ''}`}>
      <svg className={`w-5 h-5 ${activeIndex === 0 ? 'text-[#008080]' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
      </svg>
    </div>
    {/* Moments */}
    <div className={`p-2 rounded-xl ${activeIndex === 1 ? 'bg-[#008080]/10' : ''}`}>
      <svg className={`w-5 h-5 ${activeIndex === 1 ? 'text-[#008080]' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>
    </div>
    {/* Plus Button */}
    <div className="w-10 h-10 rounded-full bg-[#008080] flex items-center justify-center shadow-lg border-4 border-white">
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"/>
      </svg>
    </div>
    {/* Chat */}
    <div className={`p-2 rounded-xl ${activeIndex === 3 ? 'bg-[#008080]/10' : ''}`}>
      <svg className={`w-5 h-5 ${activeIndex === 3 ? 'text-[#008080]' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
      </svg>
    </div>
    {/* Profile */}
    <div className={`p-2 rounded-xl ${activeIndex === 4 ? 'bg-[#008080]/10' : ''}`}>
      <svg className={`w-5 h-5 ${activeIndex === 4 ? 'text-[#008080]' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
      </svg>
    </div>
  </div>
);

const NavItem = ({ icon, active }) => (
  <div className={`p-1.5 rounded-lg ${active ? 'bg-[#008080]/10' : ''}`}>
    <span className={`text-sm ${active ? 'text-[#008080]' : 'text-gray-400'}`}>{icon}</span>
  </div>
);

// Feature Card Component
const FeatureCard = ({ icon: Icon, title, description, gradient, delay, featured = false }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    whileHover={{ y: -8, transition: { duration: 0.3 } }}
    className="group relative"
    data-testid={`feature-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className={`relative rounded-2xl p-8 border transition-all duration-300 h-full ${
      featured 
        ? 'bg-gradient-to-br from-[#008080]/20 to-[#004D4D]/20 border-[#00E5FF]/30' 
        : 'bg-white/5 border-white/10 hover:border-[#008080]/30'
    }`}>
      {/* Gradient overlay on hover */}
      <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradient} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
      
      <div className="relative">
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6 ${
          featured ? 'bg-[#00E5FF]/20' : 'bg-white/10'
        }`}>
          <Icon className={`w-7 h-7 ${featured ? 'text-[#00E5FF]' : 'text-[#00E5FF]'}`} />
        </div>

        {/* Content */}
        <h3 className="font-montserrat font-bold text-xl text-white mb-3">
          {title}
          {featured && (
            <span className="ml-2 px-2 py-0.5 bg-[#00E5FF]/20 text-[#00E5FF] text-xs rounded-full font-mono">
              60ms
            </span>
          )}
        </h3>
        <p className="font-poppins text-white/60 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  </motion.div>
);
